import { formatLocaleCurrency, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { Button, Card, Popconfirm, Space } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useDeleteBailAccountSettings,
  useDeleteCommissionsLevelBailAccountSettings,
} from "../../../../../common/api/queries";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import PopconfirmDeleteIcon from "../../../../../common/components/icons/PopconfirmDeleteIcon";
import { UUID } from "../../../../../typings/global";
import { BailAccountSettings } from "../../types";

interface Props {
  settings?: BailAccountSettings;
  showActions?: boolean;
  noSettingsLabel: string;
  onCreateUpdateClick?: () => void;
  viewType?: "commissions-level" | "agent-commissions";
  parentId?: UUID;
}

export const BailAccountSettingsView = ({
  settings,
  showActions,
  noSettingsLabel,
  onCreateUpdateClick,
  viewType,
  parentId,
}: Props) => {
  const { t } = useTranslation();

  const deleteBailAccountSettings = useDeleteBailAccountSettings();
  const deleteCommissionsLevelBailAccountSettings = useDeleteCommissionsLevelBailAccountSettings();

  const actions = useMemo(() => {
    if (!showActions) {
      return undefined;
    }

    return settings
      ? [
          <Button type="link" size="small" icon={<AntIcon type="edit" />} onClick={onCreateUpdateClick}>
            {t("common.edit")}
          </Button>,
          <Popconfirm
            title={t("commissions.bailAccount.helpers.deleteSettingsConfirm")}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() => {
              if (!parentId) return;

              if (viewType === "agent-commissions") {
                deleteBailAccountSettings.mutate(parentId);
                return;
              }

              deleteCommissionsLevelBailAccountSettings.mutate(parentId);
            }}
          >
            <Button type="link" size="small" icon={<AntIcon type="delete" />} danger>
              {t("common.delete")}
            </Button>
          </Popconfirm>,
        ]
      : [
          <Button type="link" size="small" icon={<AntIcon type="plus" />} onClick={onCreateUpdateClick}>
            {t("commissions.bailAccount.actions.createSettings")}
          </Button>,
        ];
  }, [showActions, settings, onCreateUpdateClick, viewType, parentId]);

  return (
    <Card
      style={{ width: 320 }}
      title={t("commissions.bailAccount.titles.bailAccountSettings")}
      size="small"
      actions={actions}
    >
      {settings ? (
        <>
          <Space className="margin-bottom-tiny">
            <span>{t("commissions.bailAccount.attrs.targetAmount")}:</span>

            <b>{formatLocaleCurrency(settings.targetAmount)}</b>
          </Space>

          <div>
            <Space>
              <span>{t("commissions.bailAccount.attrs.rate")}:</span>

              <b>{formatLocalePercentageNumber(settings.rate)}</b>
            </Space>
          </div>
        </>
      ) : (
        <i>{noSettingsLabel}</i>
      )}
    </Card>
  );
};
